import { Edit3, FolderCog, Power, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { getError } from "@/api";
import ProductFullEditModal from "@/pages/ProductFullEditModal";

export default function ProductActionsFunctionalPanel() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [showCategories, setShowCategories] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [workingProductId, setWorkingProductId] = useState("");

  const load = () => Promise.all([api.get("/products"), api.get("/categories")])
    .then(([productResponse, categoryResponse]) => {
      setProducts(productResponse.data);
      setCategories(categoryResponse.data);
    })
    .catch((error) => toast.error(getError(error)));

  useEffect(() => {
    load();
  }, []);

  const activeCount = products.filter((product) => product.active).length;
  const inactiveCount = products.filter((product) => !product.active).length;
  const filtered = useMemo(() => {
    const normalizedQuery = query.toLocaleLowerCase("fr-FR");

    return products.filter((product) => {
      const statusMatches = statusFilter === "inactive" ? !product.active : product.active;
      const searchMatches = !normalizedQuery || [product.name, ...(product.tags || [])]
        .join(" ")
        .toLocaleLowerCase("fr-FR")
        .includes(normalizedQuery);

      return statusMatches && searchMatches;
    });
  }, [products, query, statusFilter]);

  const categoryNameFor = (categoryId) => (
    categories.find((category) => category.category_id === categoryId)?.name || "Sans catégorie"
  );

  const saveCategory = async (event) => {
    event.preventDefault();

    try {
      if (editingCategory) {
        await api.patch(`/categories/${editingCategory.category_id}`, { name: categoryName });
      } else {
        await api.post("/categories", { name: categoryName });
      }

      setCategoryName("");
      setEditingCategory(null);
      await load();
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Supprimer la catégorie ${category.name} ?`)) {
      return;
    }

    try {
      await api.delete(`/categories/${category.category_id}`);
      toast.success("Catégorie supprimée.");
      await load();
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const toggleProduct = async (product) => {
    if (workingProductId) {
      return;
    }

    const nextState = !product.active;
    setWorkingProductId(product.product_id);

    try {
      await api.patch(`/products/${product.product_id}/status`, { active: nextState });
      toast.success(nextState ? "Produit activé." : "Produit désactivé.");
      setViewing((current) => (
        current?.product_id === product.product_id ? { ...current, active: nextState } : current
      ));
      await load();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setWorkingProductId("");
    }
  };

  const deleteProduct = async (product) => {
    if (workingProductId) {
      return;
    }

    if (!window.confirm(
      `Supprimer définitivement ${product.name} du catalogue et du stock ? `
      + "Les mouvements déjà archivés seront conservés.",
    )) {
      return;
    }

    setWorkingProductId(product.product_id);

    try {
      await api.delete(`/products/${product.product_id}`);
      toast.success("Produit supprimé.");
      setViewing((current) => (
        current?.product_id === product.product_id ? null : current
      ));
      await load();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setWorkingProductId("");
    }
  };

  return (
    <section className="data-panel products-panel" data-testid="functional-product-actions-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">FICHES PRODUITS</p>
          <h2>Catalogue produits</h2>
        </div>
        <button
          aria-label="Gérer les catégories"
          data-testid="open-categories-list-button"
          onClick={() => setShowCategories((current) => !current)}
          title="Gérer les catégories"
          type="button"
        >
          <FolderCog size={20} />
        </button>
      </div>
      {showCategories && (
        <div className="quick-search-bar scrollable-list" data-testid="categories-list">
          <form onSubmit={saveCategory}>
            <input
              data-testid="category-list-name-input"
              onChange={(event) => setCategoryName(event.target.value)}
              placeholder="Nouvelle catégorie"
              required
              value={categoryName}
            />
            <button data-testid="save-category-list-button" type="submit">
              {editingCategory ? "Modifier" : "Ajouter"}
            </button>
          </form>
          {categories.map((category) => (
            <div key={category.category_id}>
              <span>{category.name}</span>
              <button
                aria-label={`Modifier ${category.name}`}
                data-testid={`edit-category-${category.category_id}`}
                onClick={() => {
                  setEditingCategory(category);
                  setCategoryName(category.name);
                }}
                type="button"
              >
                <Edit3 size={14} />
              </button>
              <button
                aria-label={`Supprimer ${category.name}`}
                data-testid={`delete-category-${category.category_id}`}
                onClick={() => deleteCategory(category)}
                type="button"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="quick-search-bar" data-testid="product-status-counts">
        <button
          className={statusFilter === "active" ? "active" : ""}
          data-testid="active-products-count-button"
          onClick={() => setStatusFilter("active")}
          type="button"
        >
          Actifs : {activeCount}
        </button>
        <button
          className={statusFilter === "inactive" ? "active" : ""}
          data-testid="inactive-products-count-button"
          onClick={() => setStatusFilter("inactive")}
          type="button"
        >
          Inactifs : {inactiveCount}
        </button>
      </div>
      <div className="quick-search-bar">
        <input
          data-testid="products-search-input"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Rechercher un produit (nom ou tag)"
          value={query}
        />
      </div>
      <div className="catalogue-products-grid scrollable-list" data-testid="product-actions-grid">
        {filtered.map((product) => (
          <article
            className="catalogue-product-card"
            data-testid={`product-card-${product.product_id}`}
            key={product.product_id}
            onClick={() => setViewing(product)}
          >
            <div className="catalogue-product-image">
              {product.photo_file_id ? (
                <img
                  alt={product.name}
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.product_id}/photo`}
                />
              ) : (
                <span>—</span>
              )}
            </div>
            <div>
              <span>{product.name}</span>
              <small>{categoryNameFor(product.category_id)}</small>
              <strong>{product.sale_price} GNF</strong>
            </div>
            <div className="product-card-actions">
              <button
                aria-label={`Modifier ${product.name}`}
                data-testid={`edit-product-${product.product_id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setEditing(product);
                }}
                type="button"
              >
                <Edit3 size={16} />
              </button>
              <button
                aria-label={product.active ? `Désactiver ${product.name}` : `Activer ${product.name}`}
                data-testid={`toggle-product-${product.product_id}`}
                disabled={workingProductId === product.product_id}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleProduct(product);
                }}
                type="button"
              >
                <Power size={16} />
              </button>
              <button
                aria-label={`Supprimer ${product.name}`}
                data-testid={`delete-product-${product.product_id}`}
                disabled={workingProductId === product.product_id}
                onClick={(event) => {
                  event.stopPropagation();
                  deleteProduct(product);
                }}
                type="button"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </article>
        ))}
      </div>
      {viewing && (
        <div className="document-modal" data-testid="product-details-modal">
          <div className="catalogue-preview-window product-details-window">
            <header>
              <div>
                <p className="eyebrow">FICHE PRODUIT</p>
                <h2>{viewing.name}</h2>
              </div>
              <button
                aria-label="Fermer la fiche produit"
                data-testid="close-product-details-button"
                onClick={() => setViewing(null)}
                type="button"
              >
                <X size={18} />
              </button>
            </header>
            <div className="product-details-content">
              <section>
                <h3>Informations</h3>
                <p>{viewing.description || "Aucune description."}</p>
                <p><strong>Catégorie</strong><span>{categoryNameFor(viewing.category_id)}</span></p>
                <p><strong>Mode d’emploi</strong><span>{viewing.usage_instructions || "Non renseigné"}</span></p>
              </section>
              <section>
                <h3>Prix</h3>
                <p><strong>Achat</strong><span>{viewing.purchase_price} GNF</span></p>
                <p><strong>Vente</strong><span>{viewing.sale_price} GNF</span></p>
                <p><strong>Minimum</strong><span>{viewing.minimum_sale_price || 0} GNF</span></p>
              </section>
            </div>
            <footer className="product-detail-actions" data-testid="product-detail-actions">
              <button
                className="secondary-action"
                data-testid={`detail-toggle-product-${viewing.product_id}`}
                disabled={workingProductId === viewing.product_id}
                onClick={() => toggleProduct(viewing)}
                type="button"
              >
                <Power size={16} /> {viewing.active ? "Désactiver" : "Activer"}
              </button>
              <button
                className="danger-action"
                data-testid={`detail-delete-product-${viewing.product_id}`}
                disabled={workingProductId === viewing.product_id}
                onClick={() => deleteProduct(viewing)}
                type="button"
              >
                <Trash2 size={16} /> Supprimer
              </button>
            </footer>
          </div>
        </div>
      )}
      {editing && (
        <ProductFullEditModal
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
          product={editing}
        />
      )}
    </section>
  );
}